/**
 * Module dependencies.
 */

import { FavoriteProps } from 'src/types/my-products';
import { GetServerSideProps, NextPage } from 'next/types';
import { Loading } from 'src/components/core';
import { Market, marketsWithoutButanePropane } from 'src/types/markets';
import { ModelRangeProps } from 'src/types/model-ranges';
import { TechnologyProps } from 'src/types/technologies';
import { dimensions } from 'src/styles/dimensions';
import { fetchFavoriteExists } from 'src/api/my-products/favorites/fetch-favorite-exists';
import { fetchMarkets } from 'src/api/markets/fetch-markets';
import { fetchModelRangeFilters } from 'src/api/model-ranges/fetch-model-range-filters';
import {
  fetchModelRanges,
  paginationLimit
} from 'src/api/model-ranges/fetch-model-ranges';

import { fetchTechnology } from 'src/api/technologies/fetch-technology';
import { parseCookies } from 'nookies';
import { useAuth } from 'src/context/auth/context';
import { useBreakpoint } from '@untile/react-components';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import { useRouter } from 'next/router';
import { useSectionsNavigation } from 'src/context/sections-navigation/context';
import FiltersListProvider from 'src/context/filters-list/provider';
import Header from 'src/components/technologies/technology-details/header';
import Metatags from 'src/components/core/metatags';
import ModelRangesList from 'src/components/model-ranges/model-ranges-list';
import RealUsageCasesSection from 'src/components/technologies/technology-details/real-usage-cases-section';
import SortProvider from 'src/context/sort/provider';
import TechnologiesCarouselSection from 'src/components/sections/technologies-carousel-section';
import TextImageSection from 'src/components/technologies/technology-details/text-image-section';
import find from 'lodash/find';
import get from 'lodash/get';
import head from 'lodash/head';
import includes from 'lodash/includes';
import last from 'lodash/last';
import pick from 'lodash/pick';
import size from 'lodash/size';
import styled from 'styled-components';
import useFavoriteExists from 'src/api/my-products/favorites/use-favorite-exists';
import useMarkets from 'src/api/markets/use-markets';
import useTechnology from 'src/api/technologies/use-technology';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  existingFavorite: FavoriteProps | null;
  markets: Market[];
  modelRangeFilters: any;
  modelRanges: {
    pageParams: number[];
    pages: Array<{
      data: ModelRangeProps[];
      nextPage: number | null;
      total: number;
    }>;
  };
  technology: TechnologyProps | null;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  min-height: 100vh;
  position: relative;
`;

/**
 * `TechnologyDetails` page.
 */

const TechnologyDetails: NextPage<Props> = props => {
  const isMobile = useBreakpoint('md', 'max');
  const locomotiveScroll = useLocomotiveScroll();
  const { markets, modelRangeFilters, modelRanges, technology } = props;
  const { query } = useRouter();
  const { token } = useAuth();
  const { translate } = useTranslate();
  const {
    data: technologyData,
    isLoading: isLoadingTechnology,
    isSuccess: isSuccessTechnology
  } = useTechnology({
    initialData: technology,
    slug: get(query, 'slug') as string
  });

  const {
    data: marketsData,
    isLoading: isLoadingMarkets,
    isSuccess: isSuccessMarkets
  } = useMarkets({ initialData: markets });

  const { data: existingFavorite, refetch: refetchExistingFavorite } =
    useFavoriteExists({
      favorableId: technologyData.id,
      initialData: props.existingFavorite,
      token
    });

  const hasButanePropaneSpecs = useMemo(() => {
    const marketType =
      find(marketsData, ['id', technologyData?.marketId])?.advisorMarketType ??
      null;

    return !includes(marketsWithoutButanePropane, marketType);
  }, [marketsData, technologyData?.marketId]);

  const isLoading = isLoadingTechnology || isLoadingMarkets;
  const isSuccess = isSuccessTechnology && isSuccessMarkets;
  const { onSetLimits, onSetSections } = useSectionsNavigation();
  const headerSectionRef = useRef<HTMLDivElement>();
  const specsSectionRef = useRef<HTMLDivElement>();
  const customizationSectionRef = useRef<HTMLDivElement>();
  const modelRangesListRef = useRef<HTMLDivElement>();
  const handleGotoModelRangesListSection = useCallback(() => {
    if (!modelRangesListRef.current) {
      return;
    }

    const offset = isMobile
      ? dimensions.navbarHeightMobile
      : dimensions.navbarHeight;

    const paddingTop = isMobile ? 32 : 128;
    const yPosition =
      modelRangesListRef.current.offsetTop - offset + paddingTop;

    locomotiveScroll.scroll.scrollTo(yPosition, 0);
  }, [isMobile, locomotiveScroll.scroll]);

  useEffect(() => {
    if (
      headerSectionRef.current &&
      specsSectionRef.current &&
      customizationSectionRef.current
    ) {
      const newSections = [
        {
          height: headerSectionRef.current.offsetHeight,
          label: 'technologies:overview',
          name: 'overview',
          yPosition: 0
        }
      ];

      if (specsSectionRef?.current) {
        newSections.push({
          height: specsSectionRef.current.offsetHeight,
          label: 'technologies:specs',
          name: 'specs',
          yPosition: specsSectionRef.current.offsetTop
        });
      }

      if (customizationSectionRef?.current) {
        newSections.push({
          height: customizationSectionRef.current.offsetHeight,
          label: 'technologies:customization',
          name: 'customization',
          yPosition: customizationSectionRef.current.offsetTop
        });
      }

      if (modelRangesListRef?.current) {
        newSections.push({
          height: modelRangesListRef.current.offsetHeight,
          label: 'technologies:modelRanges.title',
          name: 'modelRanges',
          yPosition: modelRangesListRef.current.offsetTop
        });
      }

      onSetSections(newSections);
      onSetLimits({
        end: last(newSections).yPosition + last(newSections).height * 0.2,
        start: head(newSections).yPosition + head(newSections).height * 0.6
      });
    }
  }, [onSetLimits, onSetSections]);

  return (
    <>
      <Metatags
        pageTitle={technologyData?.title}
        title={technologyData?.title}
      />

      <Wrapper>
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <Header
              {...pick(technologyData, [
                'advantagesList',
                'approvalsList',
                'awardsImages',
                'bgColor',
                'brochureFile',
                'description',
                'featuredImage',
                'gallery',
                'id',
                'slug',
                'subtitle',
                'technologyList',
                'title'
              ])}
              existingFavorite={existingFavorite}
              hasCustomizationsButton={technologyData.availableInSimulator}
              ref={headerSectionRef}
              refetchExistingFavorite={refetchExistingFavorite}
              {...(size(modelRanges?.pages[0]?.data) > 0 && {
                onGoToModelRangesSection: handleGotoModelRangesListSection
              })}
            />

            {technologyData?.specsList && technologyData?.specsImage && (
              <TextImageSection
                imageUrl={technologyData.specsImage}
                list={technologyData.specsList}
                name={'specs'}
                ref={specsSectionRef}
                title={translate('technologies:specs')}
              />
            )}

            {technologyData?.customizationList &&
              technologyData?.customizationImage && (
                <TextImageSection
                  bgColor={technologyData.bgColorCustomization}
                  imageUrl={technologyData.customizationImage}
                  isReverse
                  list={technologyData.customizationList}
                  name={'customization'}
                  ref={customizationSectionRef}
                  title={translate('technologies:customization')}
                />
              )}

            <FiltersListProvider omitUrl>
              <SortProvider omitUrl>
                <ModelRangesList
                  hasButanePropaneSpecs={hasButanePropaneSpecs}
                  modelRangeFilters={modelRangeFilters}
                  modelRanges={modelRanges}
                  ref={modelRangesListRef}
                  technologyId={technologyData.id}
                />
              </SortProvider>
            </FiltersListProvider>

            {(technologyData?.realUseCasesVideoUrl ||
              (!technologyData?.realUseCasesVideoUrl &&
                size(technologyData?.realUseCasesGallery)) > 0) && (
                <RealUsageCasesSection
                  {...pick(technologyData, [
                  'realUseCasesGallery',
                  'realUseCasesVideoImage',
                  'realUseCasesVideoUrl'
                ])}
                />
            )}

            {size(technologyData?.relatedTechnologies) > 0 && (
              <TechnologiesCarouselSection
                items={technologyData?.relatedTechnologies}
                title={translate('technologies:otherTechnologies')}
              />
            )}
          </>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<Props> = async ctx => {
  const { locale, params } = ctx;
  const markets = await fetchMarkets({ locale });
  const technology = await fetchTechnology({
    locale,
    slug: params?.slug as string
  });

  if (!technology) {
    return {
      notFound: true
    };
  }

  const modelRanges = await fetchModelRanges({
    locale,
    params: {
      limit: paginationLimit,
      page: 1
    },
    technologyId: technology?.id
  });

  const modelRangeFilters = await fetchModelRangeFilters({ locale });
  const cookies = parseCookies(ctx);
  const token = cookies?.token;
  const existingFavorite = await fetchFavoriteExists({
    favorableId: technology.id,
    token
  });

  return {
    props: {
      existingFavorite: existingFavorite ?? null,
      hasScrollDownIndicator: true,
      hasSectionsNavigation: true,
      markets,
      modelRangeFilters,
      modelRanges: {
        pageParams: [1],
        pages: [modelRanges]
      },
      technology
    }
  };
};

/**
 * Export `TechnologyDetails` page.
 */

export default TechnologyDetails;
